import {
  LESSON_VIEWER_FETCH_START,
  LESSON_VIEWER_FETCH_SUCCESS,
  LESSON_VIEWER_FETCH_ERROR,
  LESSON_VIEWER_RESET,
  LESSON_VIEWER_SET_PHASE,
  LESSON_VIEWER_ADVANCE_TERM,
  LESSON_VIEWER_SET_QUESTION_INDEX,
  LESSON_VIEWER_WORKBOOK_READY,
  LESSON_VIEWER_WORKBOOK_DONE,
  LESSON_VIEWER_WORKBOOK_SUBMIT,
  LESSON_VIEWER_KC_SUBMIT,
  LESSON_VIEWER_KC_NEXT,
  LESSON_VIEWER_KC_SUMMARY_DONE,
  LESSON_VIEWER_MASTERY_SUBMIT,
  LESSON_VIEWER_MASTERY_NEXT,
  LESSON_VIEWER_TOGGLE_DEV_TOOLS,
  LESSON_VIEWER_DEV_JUMP,
  LESSON_VIEWER_SET_TUTORIAL_VIEWED,
  LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
} from './actionTypes';

const TERM_RESET_FIELDS = {
  workbookReady: false,
  workbookDone: false,
  workbookSubmitted: false,
  workbookSelectedOption: null,
  kcAnswers: [],
  kcComplete: false,
  activeQuestionIndex: 0,
};

const INITIAL_SESSION_STATS = {
  answersTotal: 0,
  answersCorrect: 0,
  answerTimestamps: [],
};

function appendSessionAnswer(stats, correct, timestamp) {
  return {
    answersTotal: stats.answersTotal + 1,
    answersCorrect: stats.answersCorrect + (correct ? 1 : 0),
    answerTimestamps: [...stats.answerTimestamps, timestamp],
  };
}

export const initialState = {
  loading: false,
  error: null,
  rawPayload: null,
  viewerModel: null,

  phase: 'tutorial',
  activeTermIndex: 0,
  activeQuestionIndex: 0,

  workbookReady: false,
  workbookDone: false,
  workbookSubmitted: false,
  workbookSelectedOption: null,

  kcAnswers: [],
  kcComplete: false,

  masteryAnswers: [],
  masteryComplete: false,
  sessionStats: INITIAL_SESSION_STATS,

  showDevTools: true,
  tutorialViewed: false,
  tutorChatOpen: false,

  /** Web only: true until Unity legacy `x` + bridge init batch (lessonViewerUnityBridge). */
  unityWorldLoading: false,
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case LESSON_VIEWER_FETCH_START:
      return {...state, loading: true, error: null};

    case LESSON_VIEWER_FETCH_SUCCESS:
      return {
        ...initialState,
        showDevTools: state.showDevTools,
        loading: false,
        rawPayload: action.payload.rawPayload,
        viewerModel: action.payload.viewerModel,
        tutorialViewed: action.payload.tutorialViewed || false,
        phase: action.payload.tutorialViewed ? 'frontloading' : 'tutorial',
        unityWorldLoading: Boolean(action.payload.unityWorldLoading),
      };

    case LESSON_VIEWER_FETCH_ERROR:
      return {...state, loading: false, error: action.payload};

    case LESSON_VIEWER_RESET:
      return {...initialState};

    case LESSON_VIEWER_SET_PHASE:
      return {...state, phase: action.payload, tutorChatOpen: false};

    case LESSON_VIEWER_ADVANCE_TERM:
      return {
        ...state,
        activeTermIndex: action.payload,
        phase: 'learning',
        ...TERM_RESET_FIELDS,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_SET_QUESTION_INDEX:
      return {...state, activeQuestionIndex: action.payload};

    case LESSON_VIEWER_WORKBOOK_READY:
      return {...state, workbookReady: true};

    case LESSON_VIEWER_WORKBOOK_DONE:
      return {...state, workbookDone: true, phase: 'workbook_options'};

    case LESSON_VIEWER_WORKBOOK_SUBMIT:
      return {
        ...state,
        workbookSubmitted: true,
        workbookSelectedOption: action.payload.optionIndex,
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_KC_SUBMIT:
      return {
        ...state,
        kcAnswers: [
          ...state.kcAnswers,
          {
            questionIndex: action.payload.questionIndex,
            selectedAnswer: action.payload.selectedAnswer,
            correct: action.payload.correct,
          },
        ],
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_KC_NEXT:
      return {
        ...state,
        activeQuestionIndex: state.activeQuestionIndex + 1,
      };

    case LESSON_VIEWER_KC_SUMMARY_DONE: {
      const totalTerms = state.viewerModel?.terms?.length || 0;
      const nextTermIndex = state.activeTermIndex + 1;
      if (nextTermIndex < totalTerms) {
        return {
          ...state,
          activeTermIndex: nextTermIndex,
          phase: 'learning',
          ...TERM_RESET_FIELDS,
          tutorChatOpen: false,
        };
      }
      return {
        ...state,
        phase: 'mastery',
        activeQuestionIndex: 0,
        masteryAnswers: [],
        masteryComplete: false,
        tutorChatOpen: false,
      };
    }

    case LESSON_VIEWER_MASTERY_SUBMIT:
      return {
        ...state,
        masteryAnswers: [
          ...state.masteryAnswers,
          {
            questionIndex: action.payload.questionIndex,
            selectedAnswer: action.payload.selectedAnswer,
            correct: action.payload.correct,
          },
        ],
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_MASTERY_NEXT: {
      const masteryQuestions =
        state.viewerModel?.mastery?.questions || [];
      const nextIdx = state.activeQuestionIndex + 1;
      if (nextIdx < masteryQuestions.length) {
        return {...state, activeQuestionIndex: nextIdx};
      }
      return {
        ...state,
        masteryComplete: true,
        phase: 'results',
        tutorChatOpen: false,
      };
    }

    case LESSON_VIEWER_TOGGLE_DEV_TOOLS:
      return {...state, showDevTools: !state.showDevTools};

    case LESSON_VIEWER_DEV_JUMP:
      return {
        ...state,
        ...TERM_RESET_FIELDS,
        masteryAnswers: [],
        masteryComplete: false,
        sessionStats: INITIAL_SESSION_STATS,
        tutorChatOpen: false,
        ...action.payload,
      };

    case LESSON_VIEWER_SET_TUTORIAL_VIEWED:
      return {...state, tutorialViewed: true, phase: 'frontloading'};

    case LESSON_VIEWER_SET_TUTOR_CHAT_OPEN:
      return {...state, tutorChatOpen: action.payload};

    case LESSON_VIEWER_UNITY_WORLD_LOADING_SET:
      return {...state, unityWorldLoading: action.payload};

    default:
      return state;
  }
};

export default reducer;
